// @file Store for whiteboard global permissions

import type { FramePermissionOverride, Permissions } from '@@/bits/whiteboard'
import {
  doesShapeHaveAutoModerationResult,
  getFramePermissions,
  isShapeApproved,
  isShapeCreatedInCurrentTab,
  isUploadingAttachmentShape,
  PermissionLevel,
  PERMISSIONS_MAPPING,
} from '@@/bits/whiteboard'
import { VIEWER_TOOL_IDS, WRITER_TOOL_IDS } from '@@/bits/whiteboard_permissions'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { defineStore } from 'pinia'
import type { TLPageId, TLRecord, TLShape } from 'tldraw'
import { computed, ref } from 'vue'

export const useWhiteboardPermissionsStore = defineStore('whiteboardPermissions', () => {
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const whiteboardModeStore = useWhiteboardModeStore()

  const visitorPermissionLevel = computed<PermissionLevel>(() => {
    if (surfacePermissionsStore.amIOwner) {
      return PermissionLevel.Owner
    }
    if (surfacePermissionsStore.canIAdminister) {
      return PermissionLevel.Administrator
    }
    if (surfacePermissionsStore.canIEdit) {
      return PermissionLevel.Manager
    }
    if (surfacePermissionsStore.canIWrite) {
      return PermissionLevel.Writer
    }
    return PermissionLevel.Reader
  })
  const visitorPermissions = computed<Permissions>(() => PERMISSIONS_MAPPING[visitorPermissionLevel.value])

  /**
   * GLOBAL PERMISSIONS
   */

  const canIAdminister = computed(() => visitorPermissions.value.canAdminister)
  const canIManage = computed(() => visitorPermissions.value.canManage)
  const canIEdit = computed(() => visitorPermissions.value.canEdit)
  const canIWrite = computed(() => visitorPermissions.value.canWrite)

  const canIApproveShape = (shape: TLShape): boolean => {
    if (isShapeApproved(shape)) {
      // We don't need to approve an already approved shape
      return false
    }
    return canIAdminister.value
  }

  const canIViewShape = (record: TLRecord): boolean => {
    const isShapeAuthor = surfaceCurrentUserStore.currentUser?.id === record.meta.cb
    if (isUploadingAttachmentShape(record) && (!isShapeAuthor || !isShapeCreatedInCurrentTab(record))) {
      return false
    }
    if (isShapeApproved(record)) {
      return true
    }
    if (!doesShapeHaveAutoModerationResult(record)) {
      return isShapeAuthor
    }
    return isShapeAuthor || canIAdminister.value
  }

  const canIEditShape = (record: TLRecord): boolean => {
    const isShapeAuthor = surfaceCurrentUserStore.currentUser?.id === record.meta.cb
    if (isShapeAuthor || canIAdminister.value) return true
    if (!isShapeApproved(record)) return false
    return canIEdit.value
  }

  /**
   * FRAME-LEVEL PERMISSIONS
   *
   * - Admins should not be affected by frame-level overrides
   */

  const frameIdToFramePermissionOverride = ref<{ [frameId: string]: FramePermissionOverride }>({})

  const canIManageFrame = (frameId: TLPageId): boolean => {
    if (canIAdminister.value) {
      return true
    }
    const framePermissionOverride = getFramePermissions(frameId)
    return framePermissionOverride?.canManage ?? canIManage.value
  }

  const canIEditFrame = (frameId: TLPageId): boolean => {
    if (canIAdminister.value) {
      return true
    }
    const framePermissionOverride = getFramePermissions(frameId)
    return framePermissionOverride?.canEdit ?? canIEdit.value
  }

  const canIWriteFrame = (frameId: TLPageId): boolean => {
    if (canIAdminister.value) {
      return true
    }
    const framePermissionOverride = getFramePermissions(frameId)
    return framePermissionOverride?.canWrite ?? canIWrite.value
  }

  const isToolAllowed = (toolId: string, frameId: TLPageId): boolean => {
    const actualToolId =
      toolId === 'draw-nested'
        ? useWhiteboardStore().toolStates[toolId].currentSecondarySelectionId ?? 'draw-1'
        : toolId

    if (whiteboardModeStore.isReadOnlyMode) {
      return VIEWER_TOOL_IDS.includes(actualToolId)
    }
    return (WRITER_TOOL_IDS.includes(actualToolId) && canIWriteFrame(frameId)) || VIEWER_TOOL_IDS.includes(actualToolId)
  }

  const canIWriteAtLeastOneFrame = computed(() => {
    return (
      surfacePermissionsStore.canIWrite ||
      Object.keys(frameIdToFramePermissionOverride.value).some((frameId) => canIWriteFrame(frameId as TLPageId))
    )
  })

  return {
    canIManage,
    canIEdit,
    canIWrite,
    canIApproveShape,
    canIAdminister,
    canIViewShape,
    canIManageFrame,
    canIEditFrame,
    canIWriteFrame,
    canIWriteAtLeastOneFrame,
    isToolAllowed,
    frameIdToFramePermissionOverride,
    canIEditShape,
  }
})
